import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Progress,
  Table,
  Button,
  Tooltip,
  message,
  Spin,
  Modal,
} from "antd";
import {
  FiArrowLeft,
  FiPlay,
  FiPause,
  FiSquare,
  FiCheck,
  FiX,
  FiClock,
  FiActivity,
  FiTag,
  FiFileText,
  FiExternalLink,
  FiAlertCircle,
  FiRefreshCw,
  FiAward,
  FiType,
} from "react-icons/fi";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";

import {
  getBlogCampaignById,
  getBlogCampaignProgress,
  pauseBlogCampaign,
  resumeBlogCampaign,
  stopBlogCampaign,
} from "../../../../api/blog/blogCampaign";

// Status configuration
const statusConfig = {
  draft: { color: "#64748b", bg: "#f1f5f9", label: "Draft", icon: <FiClock /> },
  scheduled: { color: "#7c3aed", bg: "#f5f3ff", label: "Scheduled", icon: <FiClock /> },
  generating: { color: "#3b82f6", bg: "#eff6ff", label: "Generating", icon: <FiActivity /> },
  paused: { color: "#f59e0b", bg: "#fffbeb", label: "Paused", icon: <FiPause /> },
  completed: { color: "#10b981", bg: "#ecfdf5", label: "Completed", icon: <FiCheck /> },
  stopped: { color: "#64748b", bg: "#f1f5f9", label: "Stopped", icon: <FiSquare /> },
  failed: { color: "#ef4444", bg: "#fef2f2", label: "Failed", icon: <FiX /> },
};

// Blog status config
const blogStatusConfig = {
  pending: { color: "#64748b", bg: "#f1f5f9", label: "Pending" },
  generating: { color: "#3b82f6", bg: "#eff6ff", label: "Generating" },
  completed: { color: "#10b981", bg: "#ecfdf5", label: "Completed" },
  failed: { color: "#ef4444", bg: "#fef2f2", label: "Failed" },
};

// Campaign type labels
const campaignTypeLabels = {
  topicBased: "Topic Based",
  keywordBased: "Keyword Based",
  seriesBased: "Series Based",
};

// Format milliseconds to readable time
const formatDuration = (ms) => {
  if (!ms) return "-";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

// Format activity log action
const formatActivityAction = (action) => {
  const actions = {
    created: "Campaign created",
    scheduled: "Campaign scheduled",
    started: "Generation started",
    paused: "Campaign paused",
    resumed: "Campaign resumed",
    stopped: "Campaign stopped",
    completed: "Campaign completed",
    failed: "Campaign failed",
    blog_generated: "Blog generated",
    blog_failed: "Blog generation failed",
    tag_created: "Tag created",
    category_created: "Category created",
    titles_generated: "Titles generated",
  };
  return actions[action] || action;
};

// Get activity icon color
const getActivityColor = (action) => {
  const colors = {
    created: "#3b82f6",
    scheduled: "#7c3aed",
    started: "#10b981",
    paused: "#f59e0b",
    resumed: "#10b981",
    stopped: "#64748b",
    completed: "#10b981",
    failed: "#ef4444",
    blog_generated: "#10b981",
    blog_failed: "#ef4444",
    tag_created: "#3b82f6",
    category_created: "#7c3aed",
    titles_generated: "#3b82f6",
  };
  return colors[action] || "#64748b";
};

// Get SEO score color
const getSeoScoreColor = (score) => {
  if (score >= 80) return "#10b981";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
};

const BlogCampaignProgress = () => {
  const history = useHistory();
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const pollingRef = useRef(null);

  const fetchCampaign = async () => {
    try {
      const response = await getBlogCampaignProgress(id);
      setCampaign(response.data);
    } catch (error) {
      console.error("Error fetching campaign:", error);
      message.error("Failed to fetch campaign progress");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
    pollingRef.current = setInterval(() => {
      if (campaign?.status === "generating") {
        fetchCampaign();
      }
    }, 5000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (campaign?.status === "generating") {
      pollingRef.current = setInterval(fetchCampaign, 5000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [campaign?.status]);

  const handlePause = async () => {
    Modal.confirm({
      title: "Pause Campaign",
      content: "Are you sure you want to pause? You can resume later.",
      okText: "Pause",
      okType: "warning",
      async onOk() {
        setActionLoading(true);
        try {
          await pauseBlogCampaign(id);
          message.success("Campaign paused");
          fetchCampaign();
        } catch (error) {
          message.error("Failed to pause campaign");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await resumeBlogCampaign(id);
      message.success("Campaign resumed");
      fetchCampaign();
    } catch (error) {
      message.error("Failed to resume campaign");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    Modal.confirm({
      title: "Stop Campaign",
      content: "This action cannot be undone. Remaining blogs won't be generated.",
      okText: "Stop",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        try {
          await stopBlogCampaign(id);
          message.success("Campaign stopped");
          fetchCampaign();
        } catch (error) {
          message.error("Failed to stop campaign");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const blogColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => (
        <span style={{ color: "#64748b", fontSize: "13px" }}>{index + 1}</span>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontWeight: 500, color: "#1e293b" }}>{text || "Pending..."}</span>
          {record.blogId && (
            <Tooltip title="View blog">
              <FiExternalLink
                style={{ color: "#3b82f6", cursor: "pointer", fontSize: "14px" }}
                onClick={() => history.push(`/super-admin/blog/${record.blogId}`)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: 500,
            color: blogStatusConfig[status]?.color,
            background: blogStatusConfig[status]?.bg,
          }}
        >
          {status === "generating" && <FiRefreshCw style={{ animation: "spin 1s linear infinite" }} />}
          {blogStatusConfig[status]?.label}
        </span>
      ),
    },
    {
      title: "SEO",
      dataIndex: "seoScore",
      key: "seoScore",
      width: 70,
      render: (score) =>
        score ? (
          <span
            style={{
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#fff",
              background: getSeoScoreColor(score),
            }}
          >
            {score}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Words",
      dataIndex: "wordCount",
      key: "wordCount",
      width: 80,
      render: (count) => (
        <span style={{ color: "#64748b", fontSize: "13px" }}>
          {count ? count.toLocaleString() : "-"}
        </span>
      ),
    },
    {
      title: "Time",
      dataIndex: "generationTime",
      key: "generationTime",
      width: 80,
      render: (ms) => <span style={{ color: "#64748b", fontSize: "13px" }}>{formatDuration(ms)}</span>,
    },
  ];

  if (loading) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <FiAlertCircle style={{ fontSize: "48px", color: "#94a3b8", marginBottom: "16px" }} />
          <h2 style={{ color: "#1e293b", marginBottom: "8px" }}>Campaign Not Found</h2>
          <p style={{ color: "#64748b", marginBottom: "24px" }}>The campaign you're looking for doesn't exist.</p>
          <Button type="primary" onClick={() => history.push("/super-admin/blog-campaigns")}>
            Back to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  const { statistics = {}, generatedBlogs = [], activityLog = [] } = campaign;
  const progressPercent = statistics.totalBlogs
    ? Math.round((statistics.completedBlogs / statistics.totalBlogs) * 100)
    : 0;
  const statusInfo = statusConfig[campaign.status] || statusConfig.draft;

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px" }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <button
                onClick={() => history.push("/super-admin/blog-campaigns")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  fontSize: "14px",
                  cursor: "pointer",
                  padding: "0",
                  marginBottom: "12px",
                }}
              >
                <FiArrowLeft /> Back to Campaigns
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
                  {campaign.name}
                </h1>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: statusInfo.color,
                    background: statusInfo.bg,
                  }}
                >
                  {statusInfo.icon}
                  {statusInfo.label}
                </span>
              </div>
              <div style={{ marginTop: "8px", color: "#64748b", fontSize: "14px" }}>
                <span>{campaignTypeLabels[campaign.campaignType] || campaign.campaignType}</span>
                {campaign.topicConfig?.mainTopic && (
                  <>
                    <span style={{ margin: "0 8px" }}>•</span>
                    <span>{campaign.topicConfig.mainTopic}</span>
                  </>
                )}
                <span style={{ margin: "0 8px" }}>•</span>
                <span>Created {moment(campaign.createdAt).format("MMM D, YYYY")}</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              {campaign.status === "generating" && (
                <Button
                  icon={<FiPause />}
                  onClick={handlePause}
                  loading={actionLoading}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  Pause
                </Button>
              )}
              {campaign.status === "paused" && (
                <Button
                  type="primary"
                  icon={<FiPlay />}
                  onClick={handleResume}
                  loading={actionLoading}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  Resume
                </Button>
              )}
              {["generating", "paused"].includes(campaign.status) && (
                <Button
                  danger
                  icon={<FiSquare />}
                  onClick={handleStop}
                  loading={actionLoading}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  Stop
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          {[
            { label: "Total Blogs", value: statistics.totalBlogs || 0, icon: <FiFileText />, color: "#3b82f6" },
            { label: "Completed", value: statistics.completedBlogs || 0, icon: <FiCheck />, color: "#10b981" },
            { label: "Failed", value: statistics.failedBlogs || 0, icon: <FiX />, color: "#ef4444" },
            { label: "Pending", value: statistics.pendingBlogs || 0, icon: <FiClock />, color: "#f59e0b" },
            { label: "Avg SEO", value: statistics.averageSeoScore || 0, icon: <FiAward />, color: "#8b5cf6" },
            { label: "Total Words", value: (statistics.totalWordCount || 0).toLocaleString(), icon: <FiType />, color: "#06b6d4" },
          ].map((stat, i) => (
            <Col xs={12} sm={8} md={4} key={i}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e2e8f0",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: `${stat.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                    color: stat.color,
                    fontSize: "16px",
                  }}
                >
                  {stat.icon}
                </div>
                <div style={{ fontSize: "20px", fontWeight: 600, color: "#1e293b" }}>{stat.value}</div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>{stat.label}</div>
              </div>
            </Col>
          ))}
        </Row>

        <Row gutter={[24, 24]}>
          {/* Main Content */}
          <Col xs={24} lg={16}>
            {/* Progress Card */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                marginBottom: "24px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>Generation Progress</h3>
                  <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
                    {statistics.completedBlogs || 0} of {statistics.totalBlogs || 0} blogs generated
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#1e293b" }}>{progressPercent}%</div>
                </div>
              </div>
              <Progress
                percent={progressPercent}
                showInfo={false}
                strokeColor={{
                  "0%": "#3b82f6",
                  "100%": "#1d4ed8",
                }}
                trailColor="#e2e8f0"
                strokeWidth={10}
                style={{ marginBottom: "16px" }}
              />

              {/* Additional Stats */}
              <Row gutter={[12, 12]}>
                <Col xs={8}>
                  <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "12px", textAlign: "center", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>
                      {statistics.averageWordCount ? Math.round(statistics.averageWordCount).toLocaleString() : 0}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Avg Words/Blog</div>
                  </div>
                </Col>
                <Col xs={8}>
                  <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "12px", textAlign: "center", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>
                      {formatDuration(statistics.averageGenerationTime)}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Avg Gen Time</div>
                  </div>
                </Col>
                <Col xs={8}>
                  <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "12px", textAlign: "center", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>
                      {statistics.tagsCreated || 0}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Tags Created</div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Blogs Table */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: "0 0 16px" }}>Generated Blogs</h3>
              <Table
                columns={blogColumns}
                dataSource={generatedBlogs.map((blog, i) => ({ ...blog, key: i }))}
                pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} blogs` }}
                size="small"
                scroll={{ x: 700 }}
              />
            </div>
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            {/* Campaign Details */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "20px",
                marginBottom: "20px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: "0 0 16px" }}>Campaign Details</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {[
                  { label: "AI Provider", value: campaign.generationConfig?.aiProvider || "OpenAI" },
                  { label: "Interval", value: `${campaign.generationConfig?.minutesBetweenBlogs || 5} minutes` },
                  { label: "Word Target", value: `${campaign.contentConfig?.wordCountMin || 1500} - ${campaign.contentConfig?.wordCountMax || 2500}` },
                  { label: "FAQ Section", value: campaign.contentConfig?.includesFAQ ? `Yes (${campaign.contentConfig.faqCount || 8} Q&A)` : "No" },
                  { label: "Statistics", value: campaign.contentConfig?.includesStatistics ? "Yes" : "No" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "13px", color: "#64748b" }}>{item.label}</span>
                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Target Keywords */}
            {campaign.topicConfig?.targetKeywords?.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: "16px",
                  padding: "20px",
                  marginBottom: "20px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: "0 0 12px" }}>Target Keywords</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {campaign.topicConfig.targetKeywords.map((kw, i) => (
                    <span
                      key={i}
                      style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 500,
                        background: "#eff6ff",
                        color: "#3b82f6",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Log */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "20px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: "0 0 16px" }}>Activity Log</h3>
              <div style={{ maxHeight: "400px", overflow: "auto" }}>
                {activityLog.length === 0 ? (
                  <p style={{ color: "#94a3b8", textAlign: "center", fontSize: "13px" }}>No activity yet</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {activityLog
                      .slice()
                      .reverse()
                      .slice(0, 20)
                      .map((activity, i) => (
                        <div key={i} style={{ display: "flex", gap: "12px" }}>
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: getActivityColor(activity.action),
                              marginTop: "6px",
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
                              {formatActivityAction(activity.action)}
                            </div>
                            {activity.details && (activity.details.title || activity.details.blogTitle || activity.details.reason) && (
                              <div style={{ fontSize: "12px", color: "#64748b" }}>
                                {activity.details.title || activity.details.blogTitle || activity.details.reason}
                              </div>
                            )}
                            <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                              {moment(activity.timestamp).format("MMM D, HH:mm:ss")}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                {activityLog.length > 20 && (
                  <p style={{ color: "#94a3b8", textAlign: "center", fontSize: "12px", marginTop: "12px" }}>
                    Showing last 20 of {activityLog.length} activities
                  </p>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BlogCampaignProgress;
